/**
 * Sales Team Service
 * CRUD operations for sales positions, territory assignments, and account reviews
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  SalesPosition,
  CreateSalesPositionInput,
  UpdateSalesPositionInput,
  SalesTerritoryAssignment,
  CreateTerritoryAssignmentInput,
  AccountReview,
  CreateAccountReviewInput,
  UpdateAccountReviewInput,
  ReviewComment,
  SalesHierarchyNode,
  SalesPositionFilters,
  AccountReviewFilters,
} from "../types/sales.types";

// =====================================================
// SALES POSITIONS
// =====================================================

export async function getSalesPositions(
  filters?: SalesPositionFilters,
): Promise<SalesPosition[]> {
  let query = supabase
    .from("sales_positions")
    .select(
      `*,
      assigned_profile:profiles!sales_positions_assigned_profile_id_fkey(id, full_name, email)`,
    )
    .order("position_type")
    .order("title");

  if (filters?.position_type?.length) {
    query = query.in("position_type", filters.position_type);
  }
  if (filters?.region) {
    query = query.eq("region", filters.region);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq("is_active", filters.is_active);
  }
  if (filters?.search) {
    query = query.or(
      `title.ilike.*${filters.search}*,assigned_name.ilike.*${filters.search}*,territory.ilike.*${filters.search}*,region.ilike.*${filters.search}*`,
    );
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return (data || []) as SalesPosition[];
}

export async function getSalesPosition(
  id: UUID,
): Promise<SalesPosition | null> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select(
      `*,
      assigned_profile:profiles!sales_positions_assigned_profile_id_fkey(id, full_name, email),
      territory_assignments:sales_territory_assignments(
        *,
        organization:organizations(id, name, organization_type, address_city, address_state)
      )`,
    )
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data as SalesPosition | null;
}

export async function createSalesPosition(
  input: CreateSalesPositionInput,
  userId?: UUID,
): Promise<SalesPosition> {
  const { data, error } = await supabase
    .from("sales_positions")
    .insert({ ...input, created_by: userId, updated_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as SalesPosition;
}

export async function updateSalesPosition(
  id: UUID,
  updates: UpdateSalesPositionInput,
  userId?: UUID,
): Promise<SalesPosition> {
  const { data, error } = await supabase
    .from("sales_positions")
    .update({ ...updates, updated_by: userId })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as SalesPosition;
}

export async function deleteSalesPosition(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("sales_positions")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// HIERARCHY HELPERS
// =====================================================

/**
 * Build a tree structure from flat position list.
 * Returns root nodes (positions with no parent).
 */
export function buildHierarchyTree(
  positions: SalesPosition[],
): SalesHierarchyNode[] {
  const nodeMap = new Map<string, SalesHierarchyNode>();
  const roots: SalesHierarchyNode[] = [];

  // Create nodes
  for (const pos of positions) {
    nodeMap.set(pos.id, { position: pos, children: [], depth: 0 });
  }

  // Build tree
  for (const pos of positions) {
    const node = nodeMap.get(pos.id)!;
    if (pos.parent_position_id && nodeMap.has(pos.parent_position_id)) {
      const parent = nodeMap.get(pos.parent_position_id)!;
      node.depth = parent.depth + 1;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children by title at each level
  function sortChildren(nodes: SalesHierarchyNode[]) {
    nodes.sort((a, b) => a.position.title.localeCompare(b.position.title));
    for (const node of nodes) {
      sortChildren(node.children);
    }
  }
  sortChildren(roots);

  return roots;
}

/**
 * Get direct reports for a position
 */
export async function getDirectReports(
  positionId: UUID,
): Promise<SalesPosition[]> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select(
      `*,
      assigned_profile:profiles!sales_positions_assigned_profile_id_fkey(id, full_name, email)`,
    )
    .eq("parent_position_id", positionId)
    .eq("is_active", true)
    .order("title");

  if (error) {
    throw error;
  }
  return (data || []) as SalesPosition[];
}

/**
 * Collect a position and its entire active downline (recursive) from an
 * already-loaded flat position list — no round trip. Used by the Sales Calendar
 * territory filter to scope the calendar to a manager's subtree. Resolution runs
 * over the loaded `positions` (the calendar's active sales_rep/TM/RM/SD set), so
 * it stays consistent with what the calendar can actually display. Cycle-safe via
 * the visited set.
 */
export function collectSubtreePositionIds(
  rootId: UUID,
  positions: SalesPosition[],
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const p of positions) {
    if (p.parent_position_id) {
      const arr = childrenByParent.get(p.parent_position_id);
      if (arr) {
        arr.push(p.id);
      } else {
        childrenByParent.set(p.parent_position_id, [p.id]);
      }
    }
  }
  const result = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    for (const child of childrenByParent.get(current) ?? []) {
      if (!result.has(child)) {
        result.add(child);
        stack.push(child);
      }
    }
  }
  return result;
}

/**
 * Get unique regions from all positions
 */
export async function getRegions(): Promise<string[]> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select("region")
    .not("region", "is", null)
    .eq("is_active", true);

  if (error) {
    throw error;
  }
  const regions = [...new Set((data || []).map((d) => d.region as string))];
  return regions.sort();
}

// =====================================================
// TERRITORY ASSIGNMENTS
// =====================================================

export async function getTerritoryAssignments(
  positionId: UUID,
): Promise<SalesTerritoryAssignment[]> {
  const { data, error } = await supabase
    .from("sales_territory_assignments")
    .select(
      `*,
      organization:organizations(id, name, organization_type, address_city, address_state)`,
    )
    .eq("sales_position_id", positionId)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as SalesTerritoryAssignment[];
}

export async function getOrganizationAssignment(
  organizationId: UUID,
): Promise<SalesTerritoryAssignment[]> {
  const { data, error } = await supabase
    .from("sales_territory_assignments")
    .select(
      `*,
      sales_position:sales_positions(id, title, assigned_name, position_type)`,
    )
    .eq("organization_id", organizationId)
    .order("is_primary", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as SalesTerritoryAssignment[];
}

export async function createTerritoryAssignment(
  input: CreateTerritoryAssignmentInput,
  userId?: UUID,
): Promise<SalesTerritoryAssignment> {
  const { data, error } = await supabase
    .from("sales_territory_assignments")
    .insert({ ...input, assigned_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as SalesTerritoryAssignment;
}

export async function deleteTerritoryAssignment(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("sales_territory_assignments")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// ACCOUNT REVIEWS
// =====================================================

export async function getAccountReviews(
  filters?: AccountReviewFilters,
): Promise<AccountReview[]> {
  let query = supabase
    .from("account_reviews")
    .select(
      `*,
      organization:organizations(id, name),
      submitted_by_profile:profiles!account_reviews_submitted_by_fkey(id, full_name),
      reviewer_profile:profiles!account_reviews_reviewer_id_fkey(id, full_name),
      sales_position:sales_positions(id, title)`,
    )
    .order("review_date", { ascending: false });

  if (filters?.organization_id) {
    query = query.eq("organization_id", filters.organization_id);
  }
  if (filters?.submitted_by) {
    query = query.eq("submitted_by", filters.submitted_by);
  }
  if (filters?.status?.length) {
    query = query.in("status", filters.status);
  }
  if (filters?.date_from) {
    query = query.gte("review_date", filters.date_from);
  }
  if (filters?.date_to) {
    query = query.lte("review_date", filters.date_to);
  }
  if (filters?.search) {
    // Filter by organization name via a post-query filter since we can't ilike on joined tables easily
    // Instead, we'll fetch all and filter client-side (search is on org name)
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return (data || []) as AccountReview[];
}

export async function getAccountReview(
  id: UUID,
): Promise<AccountReview | null> {
  const { data, error } = await supabase
    .from("account_reviews")
    .select(
      `*,
      organization:organizations(id, name),
      submitted_by_profile:profiles!account_reviews_submitted_by_fkey(id, full_name),
      reviewer_profile:profiles!account_reviews_reviewer_id_fkey(id, full_name),
      last_edited_by_profile:profiles!account_reviews_last_edited_by_fkey(id, full_name),
      sales_position:sales_positions(id, title)`,
    )
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return data as AccountReview | null;
}

export async function createAccountReview(
  input: CreateAccountReviewInput,
  userId?: UUID,
): Promise<AccountReview> {
  const { data, error } = await supabase
    .from("account_reviews")
    .insert({ ...input, submitted_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AccountReview;
}

export async function updateAccountReview(
  id: UUID,
  updates: UpdateAccountReviewInput,
): Promise<AccountReview> {
  const { data, error } = await supabase
    .from("account_reviews")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AccountReview;
}

export async function deleteAccountReview(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("account_reviews")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// REVIEW COMMENTS
// =====================================================

export async function getReviewComments(
  reviewId: UUID,
): Promise<ReviewComment[]> {
  const { data, error } = await supabase
    .from("review_comments")
    .select(
      `*,
      user_profile:profiles!review_comments_user_id_fkey(id, full_name)`,
    )
    .eq("review_id", reviewId)
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }
  return (data || []) as ReviewComment[];
}

export async function createReviewComment(
  reviewId: UUID,
  commentText: string,
  userId: UUID,
): Promise<ReviewComment> {
  const { data, error } = await supabase
    .from("review_comments")
    .insert({ review_id: reviewId, user_id: userId, comment_text: commentText })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as ReviewComment;
}

export async function deleteReviewComment(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("review_comments")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

/**
 * Find the reviewer for a review based on the submitter's position hierarchy.
 * Returns the parent position's assigned_profile_id (resolves by email if needed).
 */
export async function findReviewerForPosition(
  positionId: UUID,
): Promise<UUID | null> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select("parent_position_id")
    .eq("id", positionId)
    .maybeSingle();

  if (error || !data?.parent_position_id) {
    return null;
  }

  const { data: parent, error: parentErr } = await supabase
    .from("sales_positions")
    .select("id, assigned_profile_id, assigned_email")
    .eq("id", data.parent_position_id)
    .maybeSingle();

  if (parentErr || !parent) {
    return null;
  }

  // If profile is already linked, use it
  if (parent.assigned_profile_id) {
    return parent.assigned_profile_id;
  }

  // Fallback: match parent's email to a CRM profile
  if (parent.assigned_email) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", parent.assigned_email.toLowerCase())
      .eq("is_active", true)
      .maybeSingle();

    if (profile) {
      // Backfill the link
      await supabase
        .from("sales_positions")
        .update({ assigned_profile_id: profile.id })
        .eq("id", parent.id);

      return profile.id;
    }
  }

  return null;
}

/**
 * Find the VP of Sales position (top of hierarchy).
 * Used as fallback reviewer when submitter isn't in sales hierarchy.
 * If the VP position has no assigned_profile_id, attempts to match by email.
 */
export async function findVPOfSales(): Promise<{
  id: UUID;
  assigned_profile_id: UUID | null;
} | null> {
  const { data, error } = await supabase
    .from("sales_positions")
    .select("id, assigned_profile_id, assigned_email")
    .eq("position_type", "vp")
    .eq("is_active", true)
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    return null;
  }

  // If profile is already linked, return as-is
  if (data.assigned_profile_id) {
    return data;
  }

  // Fallback: try to match the VP's email to a CRM profile
  if (data.assigned_email) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", data.assigned_email.toLowerCase())
      .eq("is_active", true)
      .maybeSingle();

    if (profile) {
      // Also backfill the link on the position for future lookups
      await supabase
        .from("sales_positions")
        .update({ assigned_profile_id: profile.id })
        .eq("id", data.id);

      return { id: data.id, assigned_profile_id: profile.id };
    }
  }

  return data;
}

// =====================================================
// MSR REP CODES (mig 2026-10-19-561, own table -- sales_positions is never altered)
// =====================================================

/** The position's rep code, or null. RLS: staff roles read all; an MSR reads only positions they may see. */
export async function getRepCode(
  salesPositionId: UUID,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("msr_rep_codes")
    .select("rep_code")
    .eq("sales_position_id", salesPositionId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data?.rep_code as string | undefined) ?? null;
}

/**
 * Set (upsert) or clear (null) a position's rep code. Only superadmin / admin / manager may (RLS refuses others).
 * A code already used by another position fails with 23505 (unique, case-insensitive).
 */
export async function setRepCode(
  salesPositionId: UUID,
  repCode: string | null,
): Promise<void> {
  const code = repCode?.trim() ?? "";
  if (code === "") {
    const { error } = await supabase
      .from("msr_rep_codes")
      .delete()
      .eq("sales_position_id", salesPositionId);
    if (error) {
      throw error;
    }
    return;
  }
  const { error } = await supabase
    .from("msr_rep_codes")
    .upsert(
      { sales_position_id: salesPositionId, rep_code: code },
      { onConflict: "sales_position_id" },
    );
  if (error) {
    throw error;
  }
}
